// Sync swatches to the active color filter in the URL.
// Calls selectColor() directly — no synthetic events — so third-party listeners
// (e.g. Amplify) aren't triggered.
(function () {
  function getActiveColor() {
    const vals = new URLSearchParams(location.search).getAll('filter.v.option.color');
    return vals[vals.length - 1] || null;
  }

  function syncSwatchesToFilter() {
    const color = getActiveColor();
    document.querySelectorAll('mntd-plp-swatches').forEach((el) => {
      if (color) {
        if (typeof el.selectColor === 'function') el.selectColor(color);
      } else {
        if (typeof el.resetToDefault === 'function') el.resetToDefault();
      }
    });
  }

  let syncTimer = null;
  function scheduleSync(delay) {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncSwatchesToFilter, delay);
  }

  // Initial load — wait for custom element to define + all instances to #init()
  customElements.whenDefined('mntd-plp-swatches').then(() => scheduleSync(50));

  // AJAX filter update — debounced so rapid selections only trigger one sync
  document.addEventListener('filter:update', () => scheduleSync(200));
})();

if (!customElements.get('mntd-plp-swatches')) {
  customElements.define('mntd-plp-swatches', class extends HTMLElement {
    #currentColor = null;
    #overlay = null;
    #overlayParent = null;
    #overlayObserver = null;
    #cardGallery = null;
    #boundHoverEnter = null;
    #boundHoverLeave = null;

    connectedCallback() {
      // Prevent clicks on the swatch area (gaps, borders) from bubbling to the card <a> link
      this.addEventListener('click', e => e.stopPropagation());
      // Defer so children (<template>) are fully parsed before we query them.
      // Cards 2+ get connectedCallback before their children are in the DOM
      // because the custom element is already defined when the opening tag is parsed.
      setTimeout(() => this.#init());
    }
    

    #init() {
      const productCard = this.closest('product-card');
      this.#cardGallery = productCard?.querySelector('[ref="cardGallery"]') || null;

      // Always register change listener — needed for fallback previewVariant path too
      this.addEventListener('change', this.#handleChange.bind(this));

      // Attempt overlay injection if alt-tagged images exist
      const template = this.querySelector('template.mntd-color-images-tpl');
      if (template && template.content.children.length > 0 && this.#cardGallery) {
        const overlay = document.createElement('div');
        overlay.className = 'mntd-color-images';
        overlay.appendChild(template.content.cloneNode(true));
        this.#overlay = overlay;

        const galleryLink = this.#cardGallery.querySelector('a[ref="cardGalleryLink"]');
        this.#overlayParent = galleryLink ?? this.#cardGallery;
        this.#overlayParent.appendChild(overlay);

        // Preload all color images immediately so they're cached before user switches swatches
        overlay.querySelectorAll('img').forEach(img => { img.loading = 'eager'; });

        // Show default color
        const checked = this.querySelector('input[type="radio"]:checked');
        if (checked) {
          this.#currentColor = checked.value;
          this.#showColor(checked.value, false);
        }

        // Custom hover only when overlay is handling images
        this.#boundHoverEnter = this.#handleHoverEnter.bind(this);
        this.#boundHoverLeave = this.#handleHoverLeave.bind(this);
        this.#cardGallery.addEventListener('pointerenter', this.#boundHoverEnter);
        this.#cardGallery.addEventListener('pointerleave', this.#boundHoverLeave);

        // Section re-renders (e.g. filter AJAX) morph card-gallery in place, stripping
        // our injected overlay without triggering connectedCallback. Re-append it.
        this.#overlayObserver = new MutationObserver((mutations) => {
          for (const m of mutations) {
            if ([...m.removedNodes].includes(this.#overlay)) {
              // Disconnect before appending to avoid re-triggering our own observer
              // or entering a loop with Amplify (which removes unrecognised child nodes).
              this.#overlayObserver.disconnect();
              this.#overlayParent.appendChild(this.#overlay);
              if (this.#currentColor) this.#showColor(this.#currentColor, false);
              requestAnimationFrame(() => {
                if (this.#overlayParent && this.#overlayObserver) {
                  this.#overlayObserver.observe(this.#overlayParent, { childList: true });
                }
              });
              break;
            }
          }
        });
        this.#overlayObserver.observe(this.#overlayParent, { childList: true });
      }
    }

    disconnectedCallback() {
      this.#overlayObserver?.disconnect();
      this.#overlayObserver = null;
      if (this.#cardGallery) {
        this.#cardGallery.removeEventListener('pointerenter', this.#boundHoverEnter);
        this.#cardGallery.removeEventListener('pointerleave', this.#boundHoverLeave);
      }
    }

    // Returns the overlay div for colorName, or null if not present
    #colorDiv(colorName) {
      if (!this.#overlay) return null;
      for (const div of this.#overlay.querySelectorAll('.mntd-color-image')) {
        if (div.dataset.color === colorName) return div;
      }
      return null;
    }

    #showColor(colorName, hovered) {
      if (!this.#overlay) return;
      for (const div of this.#overlay.querySelectorAll('.mntd-color-image')) {
        const wasActive = div.classList.contains('mntd-color-image--active');
        const match = div.dataset.color === colorName;

        if (match) {
          div.classList.remove('mntd-color-image--outgoing');
          div.classList.add('mntd-color-image--active');
          div.classList.toggle('mntd-color-image--hover', hovered);
        } else if (wasActive) {
          // Keep visible (opacity 1, z-index 0) while new image fades in on top
          div.classList.remove('mntd-color-image--active', 'mntd-color-image--hover');
          div.classList.add('mntd-color-image--outgoing');
          setTimeout(() => div.classList.remove('mntd-color-image--outgoing'), 260);
        } else {
          div.classList.remove('mntd-color-image--active', 'mntd-color-image--hover', 'mntd-color-image--outgoing');
        }
      }
    }

    #handleChange(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      this.#currentColor = input.value;
      const productCard = this.closest('product-card');

      if (this.#colorDiv(input.value)) {
        // Alt-tagged image exists — use overlay
        this.#showColor(input.value, false);
      } else {
        // No overlay div for this color — hide overlay and fall back to Horizon previewVariant
        if (this.#overlay) {
          for (const div of this.#overlay.querySelectorAll('.mntd-color-image')) {
            div.classList.remove('mntd-color-image--active', 'mntd-color-image--hover');
          }
        }
        const mediaId = input.dataset.mediaId;
        if (productCard && mediaId && typeof productCard.previewVariant === 'function') {
          productCard.previewVariant(mediaId);
        }
      }

      const variantUrl = input.dataset.variantUrl;
      if (productCard && variantUrl) {
        productCard.querySelectorAll('a[href*="/products/"]').forEach(a => { a.href = variantUrl; });
      }
    }

    // Reset to the first swatch — called when the color filter is cleared.
    resetToDefault() {
      const inputs = this.querySelectorAll('input[type="radio"]');
      const first = inputs[0];
      if (!first) return;
      first.checked = true;
      this.#currentColor = first.value;
      this.#showColor(first.value, false);
      const productCard = this.closest('product-card');
      const variantUrl = first.dataset.variantUrl;
      if (productCard && variantUrl) {
        productCard.querySelectorAll('a[href*="/products/"]').forEach((a) => { a.href = variantUrl; });
      }
    }

    // Public API used by syncSwatchesToFilter — avoids dispatching synthetic events
    // that third-party scripts (Amplify) intercept.
    selectColor(colorName) {
      const input = this.querySelector(`input[type="radio"][value="${CSS.escape(colorName)}"]`);
      if (!input) return;
      input.checked = true;
      this.#currentColor = colorName;
      const productCard = this.closest('product-card');
      if (this.#colorDiv(colorName)) {
        this.#showColor(colorName, false);
      } else {
        if (this.#overlay) {
          for (const div of this.#overlay.querySelectorAll('.mntd-color-image')) {
            div.classList.remove('mntd-color-image--active', 'mntd-color-image--hover');
          }
        }
        const mediaId = input.dataset.mediaId;
        if (productCard && mediaId && typeof productCard.previewVariant === 'function') {
          productCard.previewVariant(mediaId);
        }
      }
      const variantUrl = input.dataset.variantUrl;
      if (productCard && variantUrl) {
        productCard.querySelectorAll('a[href*="/products/"]').forEach((a) => { a.href = variantUrl; });
      }
    }

    #handleHoverEnter(event) {
      if (event.pointerType !== 'mouse' || !this.#currentColor) return;
      // Only override Horizon hover when this color has an overlay image
      if (this.#colorDiv(this.#currentColor)) this.#showColor(this.#currentColor, true);
    }

    #handleHoverLeave(event) {
      if (event.pointerType !== 'mouse' || !this.#currentColor) return;
      if (this.#colorDiv(this.#currentColor)) this.#showColor(this.#currentColor, false);
    }
  });
}
